/* eslint-disable jsx-a11y/accessible-emoji */
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { addDays } from 'date-fns/addDays';
import { isBefore } from 'date-fns/isBefore';
import { isFuture } from 'date-fns/isFuture';
import { startOfToday } from 'date-fns/startOfToday';
import {
  ExtensionAssessmentGQL,
  GetExtensionAssessmentGQLResult,
  IContact,
  IMissionCardDetails,
  LostReasonType,
  Role,
} from '@bloomays-lib/types.shared';
import MissionDetailCard from './MissionDetailCard';
import { ModifyMission } from './ModifyMission';
import { ExtensionAssessment } from './ExtensionAssessment';
import { useMutation, useQuery } from '@apollo/client';
import {
  DELETE_MISSION,
  GET_EXTENSION_ASSESSMENT,
  UPSERT_EXTESION_ASSESSMENT,
} from '@bloomays-lib/adapter.api-bloomer';
import { dateFormat, getPrivateAdminDashboardMissionPath } from '@bloomays-lib/utils.shared';
import isUndefined from 'lodash/isUndefined';
import compact from 'lodash/fp/compact';
import DeleteActionPopin from './DeleteActionPopin';
import { useTranslation } from 'react-i18next';
import { notify } from '../../helpers/toastify';
import { LoaderPending, Modal, SelectField, TypeInput, ValidationTextField } from '@bloomays-lib/ui.shared';
import { ShortenMission } from './ShortenMission';
import { CreateAmendment } from './CreateAmendment';
import { useAuthStore } from '../../stores';
import { ALL_MISSIONS } from '@bloomays-lib/adapter.api-bloomer';
import PricingHistory from './PricingHistory';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
export type PopinType = 'shorten' | 'amendment' | 'modify' | 'assessment' | 'prices' | 'delete' | null;

export type MissionCardProps = {
  mission: IMissionCardDetails; // TODO refactor --> ce type est faux ! le type de retour est lié à GET_ONE_FULL_MISSION !!
  popinToOpen: PopinType;
  resetPopinToOpen: () => void;
  legalClientContact?: IContact;
  legalClientSourceLabel?: string;
  legalBloomerContact?: IContact;
  legalBloomerSourceLabel?: string;
  legalContactUnavailableText?: string;
  /**
   * Masque la carte mission V1 (MissionDetailCard) pour ne réutiliser que l'orchestration
   * des popins. Utilisé par le tableau de bord V2 (TEC-3051) qui possède son propre en-tête
   * et son propre menu Actions, et pilote les popins via `popinToOpen`.
   */
  hideCard?: boolean;
};

type MenuOption = {
  name: string;
  action: () => void;
};

const PROLONGATION_GRACE_PERIOD_DAYS = 5;

const isWithinProlongationGracePeriod = (endMission: string): boolean => {
  const endDate = new Date(endMission);
  const today = startOfToday();
  return isBefore(endDate, today) && !isBefore(addDays(endDate, PROLONGATION_GRACE_PERIOD_DAYS), today);
};

const buildMenu = (
  navigate: NavigateFunction,
  mission: IMissionCardDetails,
  setOpenPopin: Dispatch<SetStateAction<PopinType>>,
  isSystem: boolean,
): MenuOption[] => {
  const canCreateAmendment = mission.isActive || isSystem || isWithinProlongationGracePeriod(mission.endMission);

  return compact([
    {
      name: '👀 Voir les cra',
      action: () => navigate(`${getPrivateAdminDashboardMissionPath(mission.recordId as string)}/activities`),
    },
    {
      name: '⚖️ Gérer les contrats',
      action: () => navigate(`${getPrivateAdminDashboardMissionPath(mission.recordId as string)}/contracts`),
    },
    {
      name: '📝 Gérer les factures',
      action: () => navigate(`/mission/${mission.recordId}`),
    },
    mission.isActive
      ? {
          name: '😢 Écourter la mission',
          action: () => setOpenPopin('shorten'),
        }
      : null,
    canCreateAmendment
      ? {
          name: '🥳 Créer un avenant',
          action: () => setOpenPopin('amendment'),
        }
      : null,
    mission.isActive || isFuture(new Date(mission.beginMission))
      ? {
          name: '📝  Modifier la mission',
          action: () => setOpenPopin('modify'),
        }
      : null,
    mission.isActive
      ? {
          name: '🔮 Estimer la prolongation',
          action: () => setOpenPopin('assessment'),
        }
      : null,
    mission.prices
      ? {
          name: '👀 Historique des prix',
          action: () => setOpenPopin('prices'),
        }
      : null,
    isSystem
      ? {
          name: '🛑 Supprimer mission',
          action: () => setOpenPopin('delete'),
        }
      : null,
  ]);
};

const MissionCard = ({
  mission,
  popinToOpen,
  resetPopinToOpen,
  legalClientContact,
  legalClientSourceLabel,
  legalBloomerContact,
  legalBloomerSourceLabel,
  legalContactUnavailableText,
  hideCard = false,
}: MissionCardProps) => {
  const { t } = useTranslation(['random', 'mission']);
  const navigate = useNavigate();
  const auth = useAuthStore((state) => state.auth);
  const isSystem =
    auth?.user?.roles?.includes(Role.System) || auth?.user?.email === 'erlind.sturce@bloomays.com' || false;
  const [pendingOperation, setPendingOperation] = useState<boolean>(false);

  const [openPopin, setOpenPopin] = useState<PopinType>(popinToOpen);

  const handleClose = () => {
    setOpenPopin(null);
    resetPopinToOpen();
  };

  const { data } = useQuery<GetExtensionAssessmentGQLResult>(GET_EXTENSION_ASSESSMENT, {
    variables: { missionId: mission.recordId },
  });
  const extensionAssessment: ExtensionAssessmentGQL | null = !isUndefined(data) ? data.getExtensionAssessment : null;
  const [upsertExtensionAssessmentMutation] = useMutation(UPSERT_EXTESION_ASSESSMENT);
  const upsertExtensionAssessment = async (missionId: string, assessment: number, newEndDate: Date): Promise<void> => {
    await upsertExtensionAssessmentMutation({
      variables: {
        input: {
          missionId,
          assessment,
          newEndDate: dateFormat(newEndDate, 'yyyy-MM-dd'),
        },
      },
      refetchQueries: [
        {
          query: GET_EXTENSION_ASSESSMENT,
          variables: { missionId: mission.recordId },
        },
      ],
    });
  };

  const [deleteLostReason, setDeleteLostReason] = useState<LostReasonType | ''>('');
  const [deleteOtherLostReason, setDeleteOtherLostReason] = useState('');
  const [deleteMissionMutation, { loading: loadingDeleteMission, error: errorDeleteMission, data: deleteMissionData }] =
    useMutation(DELETE_MISSION, {
      refetchQueries: [
        {
          query: ALL_MISSIONS,
          variables: {
            isActive: false,
          },
        },
      ],
    });

  const deleteMission = async (): Promise<void> => {
    if (deleteLostReason === '') {
      return;
    }
    setOpenPopin(null);
    await deleteMissionMutation({
      variables: {
        missionId: mission.recordId,
        lostReason: deleteLostReason,
        otherLostReason: deleteLostReason === LostReasonType.autre ? deleteOtherLostReason.trim() : undefined,
      },
    });
  };

  const handleCloseDelete = (): void => {
    setDeleteLostReason('');
    setDeleteOtherLostReason('');
    handleClose();
  };

  const isLostReasonNotSelected = deleteLostReason === '';
  const isDeleteOtherReasonMissing =
    deleteLostReason === LostReasonType.autre && deleteOtherLostReason.trim().length === 0;

  useEffect(() => {
    setOpenPopin(popinToOpen);
  }, [popinToOpen]);

  useEffect(() => {
    if (errorDeleteMission) {
      notify('error', errorDeleteMission.message);
    }
  }, [errorDeleteMission]);

  useEffect(() => {
    if (deleteMissionData) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteMissionData]);

  const actions = buildMenu(navigate, mission, setOpenPopin, isSystem);

  return (
    <>
      <Modal additionnalCSS={{ width: '70%' }} open={openPopin === 'shorten'} onClose={handleClose}>
        {pendingOperation ? <LoaderPending text="Attendez, ça prendra quelques secondes..." /> : null}
        <ShortenMission handleClose={handleClose} mission={mission} setPendingOperation={setPendingOperation} />
      </Modal>
      <Modal
        additionnalCSS={{ width: '70%', overflow: 'scroll' }}
        open={openPopin === 'amendment'}
        onClose={handleClose}
      >
        {pendingOperation ? (
          <LoaderPending text="Attendez, ça prendra quelques secondes. Nous lançons le processus de création d'avenant de mission..." />
        ) : null}
        <CreateAmendment
          handleClose={handleClose}
          mission={mission}
          legalClientContact={legalClientContact}
          legalClientSourceLabel={legalClientSourceLabel}
          legalBloomerContact={legalBloomerContact}
          legalBloomerSourceLabel={legalBloomerSourceLabel}
          legalContactUnavailableText={legalContactUnavailableText}
          setPendingOperation={setPendingOperation}
        />
      </Modal>
      <Modal additionnalCSS={{ width: '70%' }} open={openPopin === 'modify'} onClose={handleClose}>
        {pendingOperation ? <LoaderPending text="Attendez, on actualise la mission..." /> : null}
        <ModifyMission mission={mission} handleClose={handleClose} setPendingMissionModify={setPendingOperation} />
      </Modal>
      <Modal additionnalCSS={{ width: '75%' }} open={openPopin === 'assessment'} onClose={handleClose}>
        <ExtensionAssessment
          missionId={mission.recordId}
          currentEndDate={new Date(mission.endMission)}
          upsertExtensionAssessment={upsertExtensionAssessment}
          currentExtensionAssessment={extensionAssessment}
        />
      </Modal>

      <Modal additionnalCSS={{ width: '50%' }} open={openPopin === 'prices'} onClose={handleClose}>
        <Typography variant="h6">👀 Historique des Prix</Typography>
        <PricingHistory prices={mission.prices} />
      </Modal>
      <>
        {loadingDeleteMission && (
          <LoaderPending text="Attendez, ça prendra quelques secondes. Nous supprimons la mission d'airtable et les KPIs dans notre base..." />
        )}
        {openPopin === 'delete' && (
          <DeleteActionPopin
            opened={openPopin === 'delete'}
            onClose={handleCloseDelete}
            confirmLabel={t('deleteWarning', { ns: 'mission' })}
            deleteConfirmLabel={t('deleteLabel', { ns: 'mission' })}
            expectedValue="SUPPRIMER"
            confirmExtraDisabled={isLostReasonNotSelected || isDeleteOtherReasonMissing}
            deleteAction={() => deleteMission()}
            beforeConfirmContent={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                <SelectField
                  label={t('deleteLostReasonLabel', { ns: 'mission' })}
                  name="deleteLostReason"
                  value={deleteLostReason}
                  handlechange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setDeleteLostReason('');
                      setDeleteOtherLostReason('');
                      return;
                    }
                    const next = raw as LostReasonType;
                    setDeleteLostReason(next);
                    if (next !== LostReasonType.autre) {
                      setDeleteOtherLostReason('');
                    }
                  }}
                  options={[
                    {
                      label: t('deleteLostReasonNone', { ns: 'mission' }),
                      value: '',
                    },
                    {
                      label: t('deleteLostReasonClientAnnule', { ns: 'mission' }),
                      value: LostReasonType.client_annule_post_win,
                    },
                    {
                      label: t('deleteLostReasonBloomerAnnule', { ns: 'mission' }),
                      value: LostReasonType.bloomer_annule_post_win,
                    },
                    {
                      label: t('deleteLostReasonAutre', { ns: 'mission' }),
                      value: LostReasonType.autre,
                    },
                  ]}
                />
                {deleteLostReason === LostReasonType.autre ? (
                  <ValidationTextField
                    type={TypeInput.text}
                    label={t('deleteOtherLostReasonLabel', { ns: 'mission' })}
                    placeholder={t('deleteOtherLostReasonPlaceholder', { ns: 'mission' })}
                    value={deleteOtherLostReason}
                    onChange={(e) => setDeleteOtherLostReason(e.target.value)}
                    size="small"
                    error={false}
                  />
                ) : null}
              </Box>
            }
          />
        )}
      </>
      {!hideCard && (
        <MissionDetailCard
          type="mission"
          data={{ mission: mission, actions: actions, currentExtensionAssessment: extensionAssessment }}
        />
      )}
    </>
  );
};

export default MissionCard;
